/**
 * Dados mockados coerentes pro beta dos sócios.
 *
 * Âncoras (mês corrente):
 *   Receita Bruta R$ 18,5M; Líquida R$ 14,8M; Lucro Bruto R$ 9,2M;
 *   EBITDA R$ 5,1M (27,6%); Lucro Líquido R$ 3,6M (19,4%).
 *   Próximo 6DU: entradas R$ 14,2M, saídas R$ 9,8M, líquido +R$ 4,4M.
 *   Saldo atual em conta R$ 2,8M.
 *
 * Task #48: Fluxo já lê de /api/fluxo via FinanceiroFluxoPage.
 * DRE Gerencial migrou pro dado v2 real (tela #124, export PDF #186) —
 * `dreMesAtual`/`DreLinha`/`DrePeriodData` removidos. O que resta de mock
 * aqui: balancete da DRE Contábil (gerarBalancete, #127 ainda não migrou) e
 * os fixtures de Fluxo/liquidações usados no PDF de Fluxo e em fallbacks.
 */
import type { FluxoRow } from "../api/types";
import { _calcularProximo6duHeuristica, ymdIso } from "./dateBr";

export type BuFluxo = "atacado" | "varejo";

export interface PontoFluxoDiario {
  data: string; // ISO
  entradas: number;
  saidas: number;
  isSeisDu?: boolean;
}

export interface Liquidacao {
  cliente: string;
  tipo: "entrada" | "saida";
  valor: number;
  bu: BuFluxo;
  categoria: string;
}

// --- Fluxo de caixa 30 dias + 6DU + liquidações ------------------------

export interface SeisDuInfo {
  data: Date;
  dataIso: string;
  entradas: number;
  saidas: number;
  liquido: number;
}

export function proximoSeisDu(hoje: Date = new Date()): SeisDuInfo {
  const data = _calcularProximo6duHeuristica(hoje);
  const entradas = 14_200_000;
  const saidas = 9_800_000;
  return {
    data,
    dataIso: ymdIso(data),
    entradas,
    saidas,
    liquido: entradas - saidas,
  };
}

export function fluxo30Dias(): PontoFluxoDiario[] {
  const hoje = new Date();
  const seisDu = proximoSeisDu(hoje);
  const seisDuIso = seisDu.dataIso;

  const pontos: PontoFluxoDiario[] = [];
  // 30 dias começando 10 dias atrás, terminando 20 dias à frente (cobre 6DU
  // se estiver a <=20 dias de distância).
  for (let offset = -10; offset < 20; offset += 1) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate() + offset);
    const iso = ymdIso(d);
    const dow = d.getDay();
    const isFds = dow === 0 || dow === 6;
    const isSeis = iso === seisDuIso;

    let entradas: number;
    let saidas: number;

    if (isSeis) {
      entradas = seisDu.entradas;
      saidas = seisDu.saidas;
    } else if (isFds) {
      entradas = 0;
      saidas = 0;
    } else {
      // Variação sintética estável (baseada no dia do mês)
      const seed = (d.getDate() * 7 + d.getMonth() * 13) % 17;
      entradas = 200_000 + seed * 45_000;
      saidas = 180_000 + ((seed + 3) % 11) * 38_000;
    }

    pontos.push({ data: iso, entradas, saidas, isSeisDu: isSeis });
  }
  return pontos;
}

export function liquidacoesSeisDu(): Liquidacao[] {
  return [
    { cliente: "Cemig Distribuição", tipo: "entrada", valor: 3_450_000, bu: "atacado", categoria: "ACL" },
    { cliente: "Copel Energia", tipo: "entrada", valor: 2_890_000, bu: "atacado", categoria: "ACL" },
    { cliente: "Neoenergia Coelba", tipo: "entrada", valor: 2_120_000, bu: "atacado", categoria: "ACR" },
    { cliente: "Enel SP — Grande Consumidor", tipo: "entrada", valor: 1_780_000, bu: "atacado", categoria: "ACL" },
    { cliente: "Consórcio GD Residencial SP", tipo: "entrada", valor: 2_450_000, bu: "varejo", categoria: "GD" },
    { cliente: "Consórcio GD PME Sul", tipo: "entrada", valor: 1_510_000, bu: "varejo", categoria: "GD" },
    { cliente: "Furnas Centrais Elétricas", tipo: "saida", valor: -4_200_000, bu: "atacado", categoria: "Compra ACL" },
    { cliente: "Eletrobras — Contratos bilaterais", tipo: "saida", valor: -2_850_000, bu: "atacado", categoria: "Compra ACL" },
    { cliente: "CCEE — Liquidação mensal", tipo: "saida", valor: -1_620_000, bu: "atacado", categoria: "CCEE" },
    { cliente: "AES Brasil (PPA solar)", tipo: "saida", valor: -780_000, bu: "varejo", categoria: "GD backing" },
    { cliente: "Encargos setoriais — ONS", tipo: "saida", valor: -350_000, bu: "atacado", categoria: "Encargos" },
  ];
}

// --- KPIs Fluxo --------------------------------------------------------

export interface FluxoKpis {
  saldoAtual: number;
  entradasMes: number;
  saidasMes: number;
  saldoProjPosSeisDu: number;
}

export function fluxoKpis(): FluxoKpis {
  return {
    saldoAtual: 2_800_000,
    entradasMes: 6_400_000,
    saidasMes: 4_100_000,
    saldoProjPosSeisDu: 2_800_000 + (14_200_000 - 9_800_000),
  };
}

// --- Fluxo mensal 6m ---------------------------------------------------

/** Mensal consolidado por BU. Fix #157: split BRUTOS entradas/saidas por BU
 * (antes era so liquido `atacado`/`varejo`/`total` + heuristica `×0.72`
 * sintetica em projecaoSaldo6Meses e _calc6m — alimentava Tabela 6m e
 * chart 6m com saidas inventadas). Mantem `atacado`/`varejo` como ENTRADAS
 * brutas por BU (uso visual no chart 6m: barras empilhadas Atacado+Varejo
 * representam o lado "Entradas"); `atacado_saidas`/`varejo_saidas` sao o
 * lado "Saidas" bruto. `total_entradas`/`total_saidas` sao agregados. */
export interface PontoFluxoMensal {
  periodo: string; // "Mai/26"
  /** Entradas brutas BU atacado (= SUM(vendas)). Era liquido pre-#157. */
  atacado: number;
  /** Entradas brutas BU varejo. */
  varejo: number;
  /** Saidas brutas BU atacado (= SUM(compras)). Nao existia pre-#157. */
  atacado_saidas: number;
  /** Saidas brutas BU varejo. */
  varejo_saidas: number;
  /** atacado + varejo (consolidado). */
  total_entradas: number;
  /** atacado_saidas + varejo_saidas (consolidado). */
  total_saidas: number;
  /** Liquido mensal (`total_entradas - total_saidas`). Mantido pra
   * compat de KpiCard etc; NAO mais usado como "entradas" em
   * projecaoSaldo6Meses (#157 root cause). */
  total: number;
  saldoProjetado: number;
}

// Breakdown banco-a-banco — Indra Energia tem conta em 8 bancos (Task #33).
// Ordem fixa; BS2 é o principal. Flag `principal` preservado pra reuso
// futuro (ex: marcar conta default na integração Pluggy). Badge na UI
// foi removida em #89 — ver SaldoAtualCard.tsx.
export type SaldoPorBanco = { banco: string; valor: number; principal?: boolean };

export const saldosPorBanco: SaldoPorBanco[] = [
  { banco: "Bradesco", valor: 850000 },
  { banco: "Itaú", valor: 720000 },
  { banco: "Caixa", valor: 320000 },
  { banco: "BB", valor: 580000 },
  { banco: "Safra", valor: 230000 },
  { banco: "BS2", valor: 1500000, principal: true },
  { banco: "Banco XP", valor: 140000 },
  { banco: "Basa", valor: 60000 },
];

export const saldoAtualTotal = saldosPorBanco.reduce((s, b) => s + b.valor, 0);

// Saldo inicial de caixa pra projeções = saldo agregado (multi-banco).
export const saldoInicialCaixa = saldoAtualTotal;

export interface ProjecaoMes {
  mes: string;
  entradas: number;
  saidas: number;
  variacao: number;
  saldoFinal: number;
}

/**
 * Projecao cumulativa de saldo de caixa ao final de cada um dos N primeiros
 * meses do array mensal. Fix #157: consome `total_entradas`/`total_saidas`
 * BRUTAS direto — antes usava `m.total` (liquido) como entradas e
 * `round(m.total × 0.72)` como saidas (heuristica sintetica hardcoded copiada
 * de `gerarFluxoMensal` que produzia mock leak na Tabela 6m: Jun/26 mostrava
 * R$ 2,6M / R$ 1,8M enquanto Card 6du mostrava R$ 9,09M / R$ 4,81M reais).
 */
export function projecaoSaldo6Meses(
  saldoInicial: number,
  mensal: PontoFluxoMensal[],
): ProjecaoMes[] {
  let saldo = saldoInicial;
  return mensal.slice(0, 6).map((m) => {
    const entradas = m.total_entradas;
    const saidas = m.total_saidas;
    const variacao = entradas - saidas;
    saldo += variacao;
    return {
      mes: m.periodo,
      entradas,
      saidas,
      variacao,
      saldoFinal: saldo,
    };
  });
}

/** Mock total — fallback quando `state.rows === null` (API caiu).
 *
 * Fix #157: estrutura agora carrega entradas BRUTAS por BU + saidas BRUTAS
 * por BU separadamente. Heuristica sintetica original (atacado base 9,2M +
 * varejo base 5M crescendo 3% a.m.; saidas = 72% do total bruto) preservada
 * APENAS pra manter visual quando API offline — operador sabe que ta em
 * "modo demo" via DataSourceBadge.
 *
 * Quando API ON (caminho real), `apiRowsToMensal` substitui essa funcao
 * inteira e os valores vem do canonical_zeus_trade — heuristica nao roda. */
export function gerarFluxoMensal(meses = 6, hoje: Date = new Date()): PontoFluxoMensal[] {
  const mesesCurto = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  const baseAtacadoEntradas = 9_200_000;
  const baseVarejoEntradas = 5_000_000;
  const growth = 0.03;
  // Split sintetico saidas entre BUs proporcional ao tamanho das entradas
  // pra cada BU (~65/35) — mantem visual estavel; numeros sao demo.
  const saidasPct = 0.72;
  let saldoAcum = 2_800_000;
  const arr: PontoFluxoMensal[] = [];
  for (let i = 1; i <= meses; i += 1) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1);
    const yy = String(d.getFullYear()).slice(2);
    const atacado = Math.round(baseAtacadoEntradas * Math.pow(1 + growth, i));
    const varejo = Math.round(baseVarejoEntradas * Math.pow(1 + growth, i));
    const total_entradas = atacado + varejo;
    const total_saidas = Math.round(total_entradas * saidasPct);
    // Split saidas proporcionalmente entre BUs (so pra mock continuar
    // sendo internamente coerente — UI nao mostra BU breakdown nas saidas).
    const atacado_saidas = Math.round(total_saidas * (atacado / total_entradas));
    const varejo_saidas = total_saidas - atacado_saidas;
    saldoAcum += total_entradas - total_saidas;
    arr.push({
      periodo: `${mesesCurto[d.getMonth()]}/${yy}`,
      atacado,
      varejo,
      atacado_saidas,
      varejo_saidas,
      total_entradas,
      total_saidas,
      total: total_entradas - total_saidas,
      saldoProjetado: saldoAcum,
    });
  }
  return arr;
}

// --- Adapters: API FluxoRow → estruturas que os charts/KPIs já consomem ---
// Task #48: o backend devolve linhas (data, business_unit, entradas, saídas
// como string Decimal). Converte pra PontoFluxoDiario / PontoFluxoMensal /
// FluxoKpis sem mexer nos componentes que renderizam, mantendo
// compatibilidade com o caminho mock de fallback.

function _toN(s: string | number): number {
  const n = typeof s === "number" ? s : Number(s);
  return Number.isFinite(n) ? n : 0;
}

/** Agrega FluxoRow[] (multi-BU por dia) num PontoFluxoDiario por data.
 * `dataAlvoSeisDu` opcional (oficial CCEE via useProximo6du) marca qual
 * barra do chart 30d destacar. Quando undefined, cai na heurística
 * `proximoSeisDu().dataIso` — divergente do oficial em meses com
 * feriado móvel (Corpus Christi etc.); preferir passar a data #156. */
export function apiRowsToDiario(
  rows: FluxoRow[],
  dataAlvoSeisDu?: Date | null,
): PontoFluxoDiario[] {
  const seisDuIso = dataAlvoSeisDu ? ymdIso(dataAlvoSeisDu) : proximoSeisDu().dataIso;
  const byDate = new Map<string, { entradas: number; saidas: number }>();
  for (const r of rows) {
    const acc = byDate.get(r.data) ?? { entradas: 0, saidas: 0 };
    acc.entradas += _toN(r.entradas);
    acc.saidas += _toN(r.saidas);
    byDate.set(r.data, acc);
  }
  return Array.from(byDate.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([data, v]) => ({
      data,
      entradas: v.entradas,
      saidas: v.saidas,
      isSeisDu: data === seisDuIso,
    }));
}

/** Deriva SeisDuInfo (entradas/saídas/líquido do próximo 6º dia útil) a
 * partir de FluxoRow[]. Soma TODAS as BUs (atacado + varejo) na data alvo.
 * Devolve null se não houver linha real pra essa data — caller decide
 * render (empty state "—" honesto, NÃO mock).
 *
 * #156: signature mudou de `hoje: Date` pra `dataAlvo: Date` — caller
 * passa a data oficial CCEE (vinda de useProximo6du) em vez de
 * recomputar via heurística local, que ignora feriados móveis e
 * causava divergência de 1 dia → 0 matches → mock leak. */
export function kpis6DuFromApi(
  rows: FluxoRow[],
  dataAlvo: Date,
): SeisDuInfo | null {
  const dataIso = ymdIso(dataAlvo);
  let entradas = 0;
  let saidas = 0;
  let achou = false;
  for (const r of rows) {
    if (r.data !== dataIso) continue;
    achou = true;
    entradas += _toN(r.entradas);
    saidas += _toN(r.saidas);
  }
  if (!achou) return null;
  return { data: dataAlvo, dataIso, entradas, saidas, liquido: entradas - saidas };
}

/** KPIs do mês corrente derivados das linhas reais. saldoAtual continua
 * mock (Task #33 — integração bancária); saldoProjPosSeisDu = saldoAtual
 * mock + líquido 6DU real (zero quando não há trade — sem mock leak #156).
 *
 * `dataAlvoSeisDu` é a data oficial CCEE do próximo 6du. Quando undefined
 * ou sem trade na data, líquido = 0 (empty state honesto).
 */
export function apiRowsToKpis(
  rows: FluxoRow[],
  hoje: Date = new Date(),
  dataAlvoSeisDu?: Date | null,
  saldoAtual: number = saldoAtualTotal,
): FluxoKpis {
  const yyyymm = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
  let entradasMes = 0;
  let saidasMes = 0;
  for (const r of rows) {
    if (!r.data.startsWith(yyyymm)) continue;
    entradasMes += _toN(r.entradas);
    saidasMes += _toN(r.saidas);
  }
  const seisDuLiquido = dataAlvoSeisDu
    ? (kpis6DuFromApi(rows, dataAlvoSeisDu)?.liquido ?? 0)
    : 0;
  return {
    saldoAtual,
    entradasMes,
    saidasMes,
    saldoProjPosSeisDu: saldoAtual + seisDuLiquido,
  };
}

/** Agrega entradas/saidas BRUTAS por (mes, BU) e produz a estrutura
 * compativel com `gerarFluxoMensal`. Fix #157: antes acumulava LIQUIDO
 * (entradas - saidas) por BU, e o caller `projecaoSaldo6Meses` aplicava
 * heuristica sintetica `×0.72` em cima do liquido pra inventar saidas —
 * Tabela 6m e chart 6m exibiam valores irreais. Agora cada BU acumula
 * entradas e saidas separadamente; consumers leem brutos direto.
 *
 * Regra de negocio: gestao/compartilhada nao entram em Fluxo (so DRE). */
export function apiRowsToMensal(
  rows: FluxoRow[],
  meses = 6,
  hoje: Date = new Date(),
  saldoAtual: number = saldoInicialCaixa,
  mesInicio?: Date,
): PontoFluxoMensal[] {
  const mesesCurto = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
  interface Bucket {
    atacado_entradas: number;
    atacado_saidas: number;
    varejo_entradas: number;
    varejo_saidas: number;
  }
  const empty = (): Bucket => ({
    atacado_entradas: 0,
    atacado_saidas: 0,
    varejo_entradas: 0,
    varejo_saidas: 0,
  });
  const acc = new Map<string, Bucket>();
  for (const r of rows) {
    const key = r.data.slice(0, 7); // YYYY-MM
    const bucket = acc.get(key) ?? empty();
    const ent = _toN(r.entradas);
    const sai = _toN(r.saidas);
    if (r.business_unit === "atacado") {
      bucket.atacado_entradas += ent;
      bucket.atacado_saidas += sai;
    } else if (r.business_unit === "varejo") {
      bucket.varejo_entradas += ent;
      bucket.varejo_saidas += sai;
    }
    acc.set(key, bucket);
  }
  // Task #207: a janela ancora no `mesInicio` (1o dia do mes de CAIXA do
  // proximo 6du oficial) quando fornecido — alinha a Tabela com o Card, que
  // ancora no proximo 6du. Sem `mesInicio` (mock/legado), mantem o
  // comportamento antigo: mes seguinte ao `hoje`. O proximo 6du NUNCA eh
  // calculado aqui — vem do calendario CCEE via `mesInicio` (paridade #156/#158).
  const inicio = mesInicio
    ? new Date(mesInicio.getFullYear(), mesInicio.getMonth(), 1)
    : new Date(hoje.getFullYear(), hoje.getMonth() + 1, 1);
  let saldoAcum = saldoAtual;
  const arr: PontoFluxoMensal[] = [];
  for (let i = 0; i < meses; i += 1) {
    const d = new Date(inicio.getFullYear(), inicio.getMonth() + i, 1);
    const yyyymm = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const yy = String(d.getFullYear()).slice(2);
    const bucket = acc.get(yyyymm) ?? empty();
    const total_entradas = bucket.atacado_entradas + bucket.varejo_entradas;
    const total_saidas = bucket.atacado_saidas + bucket.varejo_saidas;
    const liquido = total_entradas - total_saidas;
    saldoAcum += liquido;
    arr.push({
      periodo: `${mesesCurto[d.getMonth()]}/${yy}`,
      atacado: Math.round(bucket.atacado_entradas),
      varejo: Math.round(bucket.varejo_entradas),
      atacado_saidas: Math.round(bucket.atacado_saidas),
      varejo_saidas: Math.round(bucket.varejo_saidas),
      total_entradas: Math.round(total_entradas),
      total_saidas: Math.round(total_saidas),
      total: Math.round(liquido),
      saldoProjetado: Math.round(saldoAcum),
    });
  }
  return arr;
}

// #246: gerarLiquidacoes6DU/gerarPagamentos2DU (+ tipos Liquidacao6du/
// Pagamento2du) removidos — só alimentavam o PDF mock de Fluxo, que saiu.

// --- Balancete DRE Contábil --------------------------------------------

export interface ContaBalancete {
  conta: string;
  saldoInicial: number;
  debito: number;
  credito: number;
  saldoFinal: number;
}

export function gerarBalancete(): ContaBalancete[] {
  const rows: Array<Omit<ContaBalancete, "saldoFinal"> & { tipo: "ativo" | "passivo" | "receita" | "despesa" }> = [
    { conta: "1.1.01 — Caixa e Equivalentes", saldoInicial: 2_450_000, debito: 14_800_000, credito: 14_450_000, tipo: "ativo" },
    { conta: "1.1.02 — Contas a Receber", saldoInicial: 8_900_000, debito: 18_500_000, credito: 17_200_000, tipo: "ativo" },
    { conta: "1.2.01 — Imobilizado", saldoInicial: 4_200_000, debito: 50_000, credito: 180_000, tipo: "ativo" },
    { conta: "2.1.01 — Fornecedores", saldoInicial: 5_600_000, debito: 9_800_000, credito: 10_420_000, tipo: "passivo" },
    { conta: "2.1.02 — Obrigações Fiscais", saldoInicial: 1_200_000, debito: 3_550_000, credito: 3_700_000, tipo: "passivo" },
    { conta: "2.2.01 — Empréstimos", saldoInicial: 3_000_000, debito: 0, credito: 0, tipo: "passivo" },
    { conta: "3.1 — Receita de Vendas", saldoInicial: 0, debito: 0, credito: 18_500_000, tipo: "receita" },
    { conta: "4.1 — Impostos sobre Vendas", saldoInicial: 0, debito: 3_700_000, credito: 0, tipo: "despesa" },
    { conta: "4.2 — CPV (Custo da Energia)", saldoInicial: 0, debito: 5_600_000, credito: 0, tipo: "despesa" },
    { conta: "5.1 — Despesas Operacionais", saldoInicial: 0, debito: 4_100_000, credito: 0, tipo: "despesa" },
  ];
  return rows.map((r) => {
    let saldoFinal: number;
    if (r.tipo === "ativo" || r.tipo === "despesa") saldoFinal = r.saldoInicial + r.debito - r.credito;
    else saldoFinal = r.saldoInicial - r.debito + r.credito;
    return {
      conta: r.conta,
      saldoInicial: r.saldoInicial,
      debito: r.debito,
      credito: r.credito,
      saldoFinal,
    };
  });
}
